// Arena background: a single quad with an analytic planet-surface grid —
// curved lat/long lines that converge toward a horizon at the view edge, an
// atmosphere rim glow, hashed stars, a slow nebula swell, and a soft
// vignette. One draw call, no textures, no simulation — a static, cheap
// cousin of pixel-td's WarpGrid (no wave solver).
//
// Torus continuity: every pattern element is exactly periodic with the arena
// size, and world samples are folded into one toroidal domain before
// evaluation, so the surface reads as continuous when the camera crosses the
// wrap seam. All motion derives from world.tick, so identical sim runs render
// identically.

import * as THREE from 'three';

// Slow pattern drift in world units per simulated second.
const DRIFT_X = 0.11;
const DRIFT_Y = 0.07;
// The pattern sticks to the camera by roughly this fraction, reading as far
// below the arena floor (fake parallax; the ortho camera gives no real depth
// parallax). Snapped per-arena so the shift across one wrap is a whole number
// of grid periods and the seam stays invisible.
const PARALLAX = 0.35;
// Planet-surface curvature: how strongly grid lines converge toward the rim.
const CURVE = 0.45;

/** Euclidean modulo; GLSL `mod` semantics for the JS-side drift wrap. */
function emod(v: number, m: number): number {
  return ((v % m) + m) % m;
}

export class BackgroundGrid {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  // World size of one fine grid cell; divides the arena exactly so the
  // pattern tiles the torus.
  private readonly cellPitch: number;
  // Drift wrap period (4 fine cells = one coarse-grid cell); wrapping the
  // drift by exactly one pattern period keeps float precision bounded over
  // long runs without any visible jump.
  private readonly driftPeriod: number;
  private readonly parallax: number;

  constructor(arenaSize: number) {
    // Fine cell count, a multiple of 4 so the 4× coarse overlay grid also
    // tiles the torus exactly.
    const cells = 4 * Math.max(1, Math.round(arenaSize / 16));
    this.cellPitch = arenaSize / cells;
    this.driftPeriod = this.cellPitch * 4;
    this.parallax =
      (Math.max(1, Math.round((PARALLAX * arenaSize) / this.driftPeriod)) * this.driftPeriod) /
      arenaSize;

    // 2× the arena so ultrawide views never see past the floor; the quad
    // follows the (unclamped) camera in update().
    const geometry = new THREE.PlaneGeometry(arenaSize * 2, arenaSize * 2);
    const starCells = Math.max(8, Math.floor(arenaSize / 2));
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uViewCenter: { value: new THREE.Vector2(0, 0) },
        uViewHalf: { value: new THREE.Vector2(40, 22.5) },
        uDrift: { value: new THREE.Vector2(0, 0) },
        uTime: { value: 0 },
        uArena: { value: arenaSize },
        uCellPitch: { value: this.cellPitch },
        // 2π / arena: nebula frequencies are integer multiples of this, so
        // the swell is exactly torus-periodic.
        uWrapFreq: { value: (Math.PI * 2) / arenaSize },
        uStarCell: { value: arenaSize / starCells },
        uStarCells: { value: starCells },
        uCurve: { value: CURVE },
        // 0..1 crossfade into the wormhole-subspace palette (purple/red
        // "other space", sparser grid). Driven by the bonus phase envelope
        // via setSubspace(); sim owns the timing, this is pure render state.
        uSub: { value: 0 }
      },
      vertexShader: PLANET_VERT,
      fragmentShader: PLANET_FRAG,
      depthTest: false,
      depthWrite: false
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.position.z = -0.5;
    this.mesh.frustumCulled = false;
    // The floor draws first; everything else overdraws it.
    this.mesh.renderOrder = -100;
    this.mesh.name = 'backgroundGrid';
  }

  /** Refresh view/drift uniforms. Call after the camera has been updated. */
  update(tick: number, alpha: number, camera: THREE.OrthographicCamera): void {
    const t = tick / 60 + alpha / 60;
    const u = this.material.uniforms;
    const cx = camera.position.x;
    const cy = camera.position.y;
    // Keep the quad under the camera (no clamping on a torus); the pattern
    // itself is anchored to folded world space in the shader, so moving the
    // quad never disturbs it.
    this.mesh.position.set(cx, cy, -0.5);
    (u.uViewCenter.value as THREE.Vector2).set(cx, cy);
    (u.uViewHalf.value as THREE.Vector2).set(
      (camera.right - camera.left) * 0.5,
      (camera.top - camera.bottom) * 0.5
    );
    const p = this.driftPeriod;
    (u.uDrift.value as THREE.Vector2).set(
      emod(cx * this.parallax + t * DRIFT_X, p),
      emod(cy * this.parallax + t * DRIFT_Y, p)
    );
    u.uTime.value = t;
  }

  /** Subspace palette crossfade (0 = normal arena, 1 = full other-space). */
  setSubspace(mix: number): void {
    this.material.uniforms.uSub.value = mix;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

const PLANET_VERT = /* glsl */ `
varying vec2 vWorld;

void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorld = wp.xy;
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

const PLANET_FRAG = /* glsl */ `
precision highp float;

uniform vec2 uViewCenter;
uniform vec2 uViewHalf;
uniform vec2 uDrift;
uniform float uTime;
uniform float uArena;
uniform float uCellPitch;
uniform float uWrapFreq;
uniform float uStarCell;
uniform float uStarCells;
uniform float uCurve;
uniform float uSub;
varying vec2 vWorld;

float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

void main() {
  // Screen-space view coordinate: 0 at the center, ±1 at the view edges.
  vec2 vd = (vWorld - uViewCenter) / uViewHalf;
  float r2 = dot(vd, vd);

  // Planet bulge: sample the surface progressively farther out toward the
  // rim, so grid lines converge as if the floor curves away to a horizon.
  vec2 samplePos = uViewCenter + vd * uViewHalf * (1.0 + uCurve * r2);

  // Fold into one toroidal domain. Every pattern below is exactly
  // uArena-periodic, so the fold — and the camera jumping across the wrap
  // seam — changes nothing visible.
  float halfArena = uArena * 0.5;
  vec2 w = mod(samplePos + halfArena, uArena) - halfArena;

  // Filter width from the unfolded sample (continuous even at the fold).
  vec2 fwQ = fwidth(samplePos) / uCellPitch * 1.5;

  // Fine square grid, derivative-antialiased, drifting slowly. The drift
  // period is exactly one coarse cell, so its JS-side wrap is invisible.
  vec2 q = (w + uDrift) / uCellPitch;
  vec2 gv = abs(fract(q) - 0.5);
  vec2 fineLines = 1.0 - smoothstep(vec2(0.5) - fwQ, vec2(0.5), gv);
  float fine = max(fineLines.x, fineLines.y);

  // Coarse overlay grid at 4× pitch for structure.
  vec2 qc = q * 0.25;
  vec2 fwC = fwQ * 0.25;
  vec2 gc = abs(fract(qc) - 0.5);
  vec2 coarseLines = 1.0 - smoothstep(vec2(0.5) - fwC, vec2(0.5), gc);
  float coarse = max(coarseLines.x, coarseLines.y);

  // All colors below are linear-working-space equivalents of the old sRGB
  // palette (OutputPass tone-maps and re-encodes); the clear color 0x050a14
  // and grid hue 0x12324a read the same as the GridHelper they replace.
  vec3 base = vec3(0.0015, 0.0030, 0.0077);
  // Slow toroidal nebula swell so empty space is not perfectly flat.
  float n = sin(w.x * uWrapFreq * 3.0 + uTime * 0.05)
          * sin(w.y * uWrapFreq * 2.0 - uTime * 0.04);
  base += vec3(0.0008, 0.0020, 0.0035) * (0.5 + 0.5 * n);

  vec3 lineColor = vec3(0.010, 0.052, 0.112);
  // Subspace crossfade: the fine grid thins out (sparser), what remains runs
  // magenta, the floor sinks into a deep violet-red murk, and the nebula
  // swell turns blood-warm — the plane must read as somewhere else instantly.
  vec3 subBase = vec3(0.0105, 0.0012, 0.0140);
  subBase += vec3(0.0035, 0.0006, 0.0022) * (0.5 + 0.5 * n);
  vec3 subLineColor = vec3(0.115, 0.014, 0.135);
  float fineWeight = mix(0.34, 0.10, uSub);
  float coarseWeight = mix(0.22, 0.30, uSub);
  vec3 col = mix(base, subBase, uSub)
           + mix(lineColor, subLineColor, uSub) * (fine * fineWeight + coarse * coarseWeight);

  // Stars: one hashed point per ~2 u cell; cell ids fold with the torus.
  // Kept deliberately dim and sparse so the speckle layer stays well below
  // the enemy luminance floor — background must never read as a false enemy.
  vec2 sp = w / uStarCell;
  vec2 cellId = mod(floor(sp), vec2(uStarCells));
  vec2 fp = fract(sp) - 0.5;
  float sh = hash12(cellId);
  vec2 sOff = (vec2(hash12(cellId + 17.13), hash12(cellId + 41.71)) - 0.5) * 0.7;
  float sd = length(fp - sOff) * uStarCell;
  float sw = clamp(fwidth(samplePos.x) + fwidth(samplePos.y), 0.02, 0.35);
  float star = smoothstep(0.06 + sw, 0.0, sd) * step(0.955, sh);
  float twinkle = 0.6 + 0.4 * sin(uTime * 1.7 + sh * 39.0);
  vec3 starColor = mix(vec3(0.11, 0.14, 0.20), vec3(0.20, 0.06, 0.10), uSub);
  col += starColor * star * twinkle;

  // Atmosphere rim glow, then horizon darkening that doubles as the soft
  // vignette around the current view.
  float r = sqrt(r2);
  col += mix(vec3(0.010, 0.042, 0.070), vec3(0.070, 0.008, 0.045), uSub) * smoothstep(0.55, 1.05, r);
  col *= 1.0 - 0.38 * smoothstep(0.85, 1.30, r);

  gl_FragColor = vec4(col, 1.0);
}
`;
